import React, { createContext, useContext, useEffect, useReducer, useState, useRef, type FC, type ReactNode } from "react";
import { reducer } from "./trackReducer";
import { INITIAL_STATE } from "./trackInitialState";
import { ITrackContextValue } from "./trackContextInterface";
import { getAdminIdFromStoreFrontId } from "@/shared/utils/shopify";
import { sortBySku } from "@/shared/utils/utils";
import { TRACK_VARIANT_CONFIGS } from "@/shared/utils/trackConfig";
import { calculateDistanceFromVenueFree, calculateLocationDiscount } from "@/shared/utils/freeDistanceCalculator";
import { TVisitorLocation } from "@/types";
import { getPostcodeCookie, hasValidPostcodeCookie, setPostcodeCookie, clearPostcodeCookie } from "@/shared/utils/cookieOperations";
import getCartData from "@/shared/utils/getCartData";
import { fetchProductsByMonth } from "@/shared/utils/productOperations";
import { DateTime } from "luxon";

const TrackContext = createContext<ITrackContextValue | null>(null);

const useTrackContext = () => {
  const context = useContext(TrackContext);
  if (!context) {
    throw new Error("useTrackContext must be used within a TrackProvider");
  }
  return context;
};

const TrackProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const {
    battleKartSelection,
    mainTrackSelection,
    miniTrackSection,
    juniorTrackSelection,
    intermediateTrackSelection,
    productRenderedLocation,
    shopifyCartVariants,
    customerInfo,
    headlessCartInfo,
    membershipInfo,
    mainTrackSelectionByAgeGroup,
  } = state;
  const [isValidating, setIsValidating] = useState(false);
  const [isMemberChecked, setIsMemberChecked] = useState(false);
  const hasRestoredMainTrackByAgeGroupRef = useRef<Record<string, boolean>>({});

  // Restore main track selection per age group (e.g. after redirect to root)
  useEffect(() => {
    const ageGroupKey = mainTrackSelection.ageGroup || "unknown";
    if (hasRestoredMainTrackByAgeGroupRef.current[ageGroupKey]) return;
    if (mainTrackSelection.variantsAndDate.selectedVariants.length > 0) {
      hasRestoredMainTrackByAgeGroupRef.current[ageGroupKey] = true;
      return;
    }
    const saved = mainTrackSelectionByAgeGroup?.[ageGroupKey];

    if (saved && Array.isArray(saved.selectedVariants) && saved.selectedVariants.length > 0) {
      dispatch({
        type: "SET_DATE_AND_VARIANT",
        payload: {
          variantsAndDate: {
            ...mainTrackSelection.variantsAndDate,
            selectedVariants: saved.selectedVariants,
            selectedDate: saved.selectedDate || mainTrackSelection.variantsAndDate.selectedDate,
            selectedSessionTime: saved.selectedSessionTime || mainTrackSelection.variantsAndDate.selectedSessionTime,
          },
        },
      });
    }

    hasRestoredMainTrackByAgeGroupRef.current[ageGroupKey] = true;
  }, [dispatch, mainTrackSelection.ageGroup, mainTrackSelection.variantsAndDate, mainTrackSelectionByAgeGroup]);

  // Persist main track selection per age group in context
  useEffect(() => {
    const { selectedVariants, selectedDate, selectedSessionTime } = mainTrackSelection.variantsAndDate;
    if (!Array.isArray(selectedVariants)) return;

    const ageGroupKey = mainTrackSelection.ageGroup || "unknown";
    const existing = mainTrackSelectionByAgeGroup?.[ageGroupKey];
    const hasSameSelection =
      existing &&
      JSON.stringify(existing.selectedVariants) === JSON.stringify(selectedVariants) &&
      String(existing.selectedDate) === String(selectedDate) &&
      existing.selectedSessionTime === selectedSessionTime;

    if (hasSameSelection) return;

    dispatch({
      type: "SET_MAIN_TRACK_SELECTION_BY_AGE_GROUP",
      payload: {
        selectionsByAgeGroup: {
          ...mainTrackSelectionByAgeGroup,
          [ageGroupKey]: {
            selectedVariants,
            selectedDate,
            selectedSessionTime,
            selectedVariantsForUpsell: mainTrackSelection.variantsAndDate.selectedVariantsForUpsell,
          },
        },
      },
    });
  }, [dispatch, mainTrackSelection.ageGroup, mainTrackSelection.variantsAndDate, mainTrackSelectionByAgeGroup]);

  // Initialize postcode from cookies on component mount
  useEffect(() => {
    try {
      if (hasValidPostcodeCookie()) {
        const savedPostcodeData = getPostcodeCookie();

        if (
          savedPostcodeData &&
          savedPostcodeData.suburb &&
          savedPostcodeData.state &&
          savedPostcodeData.postcode &&
          savedPostcodeData.coordinates &&
          typeof savedPostcodeData.distance === "number" &&
          typeof savedPostcodeData.discountAmount === "number"
        ) {
          // Recompute the discount on restore so that pricing-rule changes
          // (e.g. new premium-postcode tiers) take effect immediately for
          // returning visitors instead of waiting for the 12-month cookie
          // to expire.
          const recomputedDiscount = calculateLocationDiscount(savedPostcodeData.postcode, savedPostcodeData.distance);

          const visitorLocation: TVisitorLocation = {
            suburb: savedPostcodeData.suburb,
            state: savedPostcodeData.state,
            postcode: savedPostcodeData.postcode,
            coordinates: savedPostcodeData.coordinates,
            distance: savedPostcodeData.distance,
            discountAmount: recomputedDiscount,
          };

          if (recomputedDiscount !== savedPostcodeData.discountAmount) {
            setPostcodeCookie({
              ...savedPostcodeData,
              discountAmount: recomputedDiscount,
              timestamp: new Date().toISOString(),
            });
          }

          dispatch({
            type: "SET_VISITOR_LOCATION",
            payload: { visitorLocation },
          });

          dispatch({
            type: "SET_HAS_PROVIDED_POSTCODE",
            payload: { hasProvidedPostcode: true },
          });
        } else {
          // Clear corrupted cookie data
          console.warn("Corrupted postcode cookie data found, clearing cookie");
          clearPostcodeCookie();
        }
      }
    } catch (error) {
      console.error("Error initializing postcode from cookie:", error);
      // Clear potentially corrupted cookie
      clearPostcodeCookie();
    }
  }, [dispatch]);

  useEffect(() => {
    const validatePostcode = async () => {
      if (customerInfo.customerPostalCode && customerInfo.customerPostalCode.length === 4 && /^\d{4}$/.test(customerInfo.customerPostalCode) && !isValidating) {
        setIsValidating(true);
        const inputValue = customerInfo.customerPostalCode;
        try {
          const distanceResult = await calculateDistanceFromVenueFree(inputValue);

          if (distanceResult.visitorLocation && distanceResult.distance && distanceResult.distance.kilometers > 0) {
            const discountPercentage = calculateLocationDiscount(distanceResult.visitorLocation.postcode, distanceResult.distance.kilometers);
            const visitorLocation: TVisitorLocation = {
              suburb: distanceResult.visitorLocation.suburb,
              state: distanceResult.visitorLocation.state,
              postcode: distanceResult.visitorLocation.postcode,
              coordinates: distanceResult.visitorLocation.coordinates,
              distance: distanceResult.distance.kilometers,
              discountAmount: discountPercentage,
            };

            dispatch({
              type: "SET_VISITOR_LOCATION",
              payload: { visitorLocation },
            });

            // Save postcode to cookies for 12 months
            setPostcodeCookie({
              postcode: distanceResult.visitorLocation.postcode,
              suburb: distanceResult.visitorLocation.suburb,
              state: distanceResult.visitorLocation.state,
              coordinates: distanceResult.visitorLocation.coordinates,
              distance: distanceResult.distance.kilometers,
              discountAmount: discountPercentage,
              timestamp: new Date().toISOString(),
            });

            // Mark that user has provided postcode
            dispatch({
              type: "SET_HAS_PROVIDED_POSTCODE",
              payload: { hasProvidedPostcode: true },
            });
          }
        } catch (error) {
          console.error("Postcode validation failed:", error);
        }
      }
    };

    validatePostcode();
  }, [customerInfo.customerPostalCode, dispatch, isValidating, state.visitorLocation.suburb]);

  useEffect(() => {
    const handleCartUpdated = (event: CustomEvent) => {
      if (productRenderedLocation !== "external") {
        const {
          detail: { cart },
        } = event;

        const { items_subtotal_price } = cart;

        if (items_subtotal_price) {
          dispatch({
            type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
            payload: {
              headlessCartTotalAmount: items_subtotal_price,
            },
          });
        } else {
          dispatch({
            type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
            payload: {
              headlessCartTotalAmount: "0",
            },
          });
        }

        if (cart && cart.items && cart.items.length > 0) {
          dispatch({
            type: "SET_IS_ANY_ITEMS_AVAILABLE_IN_CART",
            payload: { isAnyItemsAvailableInCart: true },
          });

          const cartVariantIds = cart.items.map((item: any) => Number(item.variant_id ?? item.id));

          // Sync each track's selected variants against the current cart contents.
          // Config flags control whether to clear selections when no matches are found
          // (see TRACK_VARIANT_CONFIGS documentation for behavioral details).
          for (const config of TRACK_VARIANT_CONFIGS) {
            const trackState = (state as any)[config.stateKey];
            const variantsAndDate = trackState.variantsAndDate;
            const filtered = variantsAndDate.selectedVariants.filter((item: any) => cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id)));

            if (filtered.length > 0) {
              dispatch({
                type: config.setVariantAction as any,
                payload: {
                  variantsAndDate: {
                    ...variantsAndDate,
                    selectedVariants: sortBySku([...filtered], config.trackType),
                  },
                },
              });
            } else if (config.dispatchWhenFilteredEmpty) {
              dispatch({
                type: config.setVariantAction as any,
                payload: {
                  variantsAndDate: { ...variantsAndDate, selectedVariants: [] },
                },
              });
            }
          }

          const updatedShopifyCartVariants = shopifyCartVariants.filter((variant: any) => cartVariantIds.includes(getAdminIdFromStoreFrontId(variant.variantsSelectedObject.id)));
          dispatch({
            type: "SET_SHOPIFY_CART_VARIANTS",
            payload: { shopifyCartVariants: updatedShopifyCartVariants },
          });
        } else {
          dispatch({
            type: "SET_IS_ANY_ITEMS_AVAILABLE_IN_CART",
            payload: { isAnyItemsAvailableInCart: false },
          });
          dispatch({
            type: "SET_SHOPIFY_CART_VARIANTS",
            payload: { shopifyCartVariants: [] },
          });

          // Clear each track's variants when cart is empty, respecting
          // per-track clearWhenCartEmpty flags (main track preserves selections).
          for (const config of TRACK_VARIANT_CONFIGS) {
            if (config.clearWhenCartEmpty) {
              const trackState = (state as any)[config.stateKey];
              dispatch({
                type: config.setVariantAction as any,
                payload: {
                  variantsAndDate: { ...trackState.variantsAndDate, selectedVariants: [] },
                },
              });
            }
          }
        }
      }
    };
    window.addEventListener("cart:updated", handleCartUpdated as EventListener);

    return () => {
      window.removeEventListener("cart:updated", handleCartUpdated as EventListener);
    };
  }, [
    dispatch,
    productRenderedLocation,
    battleKartSelection.variantsAndDate,
    juniorTrackSelection.variantsAndDate,
    intermediateTrackSelection.variantsAndDate,
    mainTrackSelection.variantsAndDate,
    miniTrackSection.variantsAndDate,
    shopifyCartVariants,
    state,
  ]);

  useEffect(() => {
    const fetchCartData = async () => {
      if (!isMemberChecked) {
        let lineItems: any[] = [];

        // Handle both external (headless) and internal (Shopify) cart
        if (productRenderedLocation === "external" && headlessCartInfo.id !== "" && headlessCartInfo.checkoutUrl !== "") {
          const cartData = await getCartData(productRenderedLocation, headlessCartInfo);
          if (cartData) {
            const { cart } = cartData;
            if (cart) {
              const { lines, items } = cart;
              if (items && items.length > 0) {
                lineItems = items;
              }
              if (lines) {
                const { edges } = lines;
                if (edges) {
                  lineItems = edges.map((edge: any) => ({ ...edge.node.merchandise, headlessCartId: edge.node.id }));
                }
              }
            }
          }
        } else if (productRenderedLocation === "internal") {
          // For internal Shopify cart, fetch cart.json
          try {
            const response = await fetch("/cart.json");
            if (response.ok) {
              const contentType = response.headers.get("content-type");
              // Check if response is actually JSON
              if (contentType && contentType.includes("application/json")) {
                const data = await response.json();
                lineItems = data.items || [];
              } else {
                // Response is not JSON (probably HTML error page), treat as empty cart
                lineItems = [];
              }
            } else {
              // Non-OK response, treat as empty cart (no need to log, this is expected for new sessions)
              lineItems = [];
            }
          } catch (error) {
            // Silently handle errors - cart might not exist yet, which is normal
            lineItems = [];
          }
        }

        // Check for membership items
        if (lineItems.length > 0) {
          const isMembershipItemAddedToCart = lineItems.some(
            (item: any) => item.product_title?.toLowerCase().includes("membership") || item.product?.title?.toLowerCase().includes("membership") || item.sku === "PADDOCKCLUB",
          );
          dispatch({
            type: "SET_MEMBERSHIP_INFO",
            payload: { membershipInfo: { ...membershipInfo, isMembershipItemAddedToCart: isMembershipItemAddedToCart } },
          });
        } else {
          // Ensure membership is set to false if no items in cart
          dispatch({
            type: "SET_MEMBERSHIP_INFO",
            payload: { membershipInfo: { ...membershipInfo, isMembershipItemAddedToCart: false } },
          });
        }

        // Set cart availability
        dispatch({
          type: "SET_IS_ANY_ITEMS_AVAILABLE_IN_CART",
          payload: {
            isAnyItemsAvailableInCart: lineItems.length > 0,
          },
        });
      }
      setIsMemberChecked(true);
    };
    fetchCartData();
  }, [headlessCartInfo, isMemberChecked, membershipInfo, productRenderedLocation]);

  // Background prefetch: start fetching collection products as soon as a track is selected
  const prefetchedTrackRef = useRef<string | null>(null);
  const { trackSelectionOption, collectionProducts: collectionProductsState } = state;

  useEffect(() => {
    const selectedTrack = trackSelectionOption.title;
    if (!selectedTrack || selectedTrack === "RACING_GAMES_TRACK" || selectedTrack === "ARCADE") return;

    // Skip if we already prefetched for this track or data already exists
    if (prefetchedTrackRef.current === selectedTrack) return;
    if (collectionProductsState && collectionProductsState.track === selectedTrack) return;

    prefetchedTrackRef.current = selectedTrack;

    // Fire and forget — runs in background, doesn't block rendering
    fetchProductsByMonth(selectedTrack, DateTime.now())
      .then((fetchedProducts) => {
        if (fetchedProducts) {
          dispatch({
            type: "SET_COLLECTION_PRODUCTS",
            payload: {
              collectionProducts: {
                track: selectedTrack,
                collectionProducts: fetchedProducts,
              },
            },
          });
        }
      })
      .catch((error) => {
        console.error("Background prefetch failed:", error);
      });
  }, [dispatch, trackSelectionOption.title, collectionProductsState]);

  return <TrackContext.Provider value={{ ...state, dispatch }}>{children}</TrackContext.Provider>;
};

export { useTrackContext, TrackProvider };
